import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowRight, Check, LogIn, LifeBuoy, ExternalLink, Info } from "lucide-react";
import { PageShell } from "@/components/site/page-shell";
import { pageData } from "@/lib/service-page-data";
import { useCurrentPageOverride } from "@/hooks/use-page-override";
import { getWhmcsPortalUrl } from "@/lib/whmcs.functions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const data = pageData["whmcs"];

export const Route = createFileRoute("/whmcs")({
  component: WhmcsPage,
  head: () => ({
    meta: [
      { title: `${data.title} — CoderLity` },
      { name: "description", content: data.sub },
      { property: "og:title", content: `${data.title} — CoderLity` },
      { property: "og:description", content: data.sub },
      { property: "og:url", content: "/whmcs" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/whmcs" }],
  }),
});

function WhmcsPage() {
  const { data: override } = useCurrentPageOverride();
  const eyebrow = override?.hero_eyebrow || data.eyebrow;
  const title = override?.hero_title || data.title;
  const sub = override?.hero_sub || data.sub;

  const fetchPortal = useServerFn(getWhmcsPortalUrl);
  const { data: portal, isLoading } = useQuery({
    queryKey: ["whmcs-portal-url"],
    queryFn: () => fetchPortal(),
    staleTime: 5 * 60 * 1000,
  });

  const [demoOpen, setDemoOpen] = useState(false);

  const handleLogin = (e: React.MouseEvent) => {
    if (portal?.configured && portal.url) return; // let anchor navigate
    e.preventDefault();
    setDemoOpen(true);
  };

  const loginHref = portal?.configured && portal.url ? portal.url : "#";
  const loginLabel = isLoading ? "Loading…" : portal?.configured ? "Client login" : "View demo portal";

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 -z-10 bg-hero-glow opacity-60" />
        {override?.cover_url && (
          <div className="absolute inset-0 -z-20 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${override.cover_url})` }} />
        )}
        <div className="mx-auto max-w-6xl px-4 lg:px-8 py-14 sm:py-20">
          <div className="grid grid-cols-[minmax(0,1fr)] gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-widest text-primary">{eyebrow}</div>
              <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">{title}</h1>
              <p className="mt-4 max-w-2xl text-muted-foreground">{sub}</p>
              {!isLoading && !portal?.configured && (
                <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5" /> Demo mode — connect your WHMCS instance to auto-enable the live portal link.
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={loginHref}
                onClick={handleLogin}
                target={portal?.configured ? "_blank" : undefined}
                rel={portal?.configured ? "noopener noreferrer" : undefined}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-brand px-5 py-2.5 text-sm font-semibold text-white shadow-glow"
              >
                <LogIn className="h-4 w-4" /> {loginLabel}
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/40">
                <LifeBuoy className="h-4 w-4" /> Get help
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-border/60">
        <div className="mx-auto max-w-6xl px-4 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-4">
            <h2 className="min-w-0 text-2xl sm:text-3xl font-bold tracking-tight">Everything in one portal</h2>
            <Link to="/contact" className="shrink-0 text-sm font-semibold text-primary hover:underline">Request a demo →</Link>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.features.map((f) => (
              <div key={f.title} className="group rounded-2xl border border-border bg-card p-5 transition hover:border-primary/40 hover:shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-semibold">{f.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-b border-border/60 bg-muted/20">
        <div className="mx-auto max-w-6xl px-4 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Simple pricing</h2>
            <p className="mt-2 text-sm text-muted-foreground">Portal is free with every service. Upgrade only if you need it.</p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {data.pricing.map((p) => (
              <div key={p.name} className={`flex flex-col rounded-2xl border p-6 ${p.highlighted ? "border-primary bg-primary/5 shadow-glow" : "border-border bg-card"}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="truncate text-base font-semibold">{p.name}</h3>
                  {p.highlighted && (
                    <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">Popular</span>
                  )}
                </div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{p.price}</span>
                  {p.period && <span className="text-sm text-muted-foreground">/ {p.period}</span>}
                </div>
                <ul className="mt-4 flex-1 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold ${p.highlighted ? "bg-gradient-brand text-white shadow-glow" : "border border-border hover:border-primary/40"}`}>
                  Get started <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-border/60">
        <div className="mx-auto max-w-4xl px-4 lg:px-8 py-12 sm:py-16">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">FAQ</h2>
          <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
            {data.faqs.map((f) => (
              <details key={f.q} className="group p-5 open:bg-muted/20">
                <summary className="flex cursor-pointer items-center justify-between gap-4 text-base font-semibold">
                  <span className="min-w-0">{f.q}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 transition group-open:rotate-90" />
                </summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="mx-auto max-w-6xl px-4 lg:px-8 py-14">
          <div className="grid grid-cols-[minmax(0,1fr)] gap-6 rounded-3xl border border-border bg-gradient-brand/10 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div className="min-w-0">
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">Ready to manage everything from one place?</h3>
              <p className="mt-1 text-sm text-muted-foreground">Log in to your client portal or talk to us about a custom setup.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={loginHref}
                onClick={handleLogin}
                target={portal?.configured ? "_blank" : undefined}
                rel={portal?.configured ? "noopener noreferrer" : undefined}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-brand px-5 py-2.5 text-sm font-semibold text-white shadow-glow"
              >
                {loginLabel} <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/40">
                Contact sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Demo dialog — shown when WHMCS not yet connected */}
      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Client portal — demo preview</DialogTitle>
            <DialogDescription>
              The live WHMCS portal isn't connected yet. Once your admin adds the WHMCS API URL in Settings, the "Client login" button will automatically point to your real portal.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm">
            <div className="font-semibold">Demo credentials</div>
            <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground">
              <span>Email</span><code className="text-foreground">demo@coderlity.com</code>
              <span>Password</span><code className="text-foreground">demo1234</code>
            </div>
            <p className="mt-3 text-xs">Read-only sample account. No real invoices or services.</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDemoOpen(false)}>Close</Button>
            <a
              href="https://demo.whmcs.com/index.php?rp=/login"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-gradient-brand px-4 py-2 text-sm font-semibold text-white shadow-glow"
            >
              Open demo <ExternalLink className="h-4 w-4" />
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
